/**
 * Single source of truth for ops-console environment names.
 *
 * Adding or removing an environment should be a one-line change here; call
 * sites import these constants/helpers instead of inlining "dev" / "staging"
 * literals or re-implementing the `VITE_ENV_NAME` read. The per-`EnvName`
 * `Record` maps elsewhere (badge colors/labels, test-inbox URLs) are typed
 * against `EnvName`, so adding an environment surfaces a compile error at every
 * site that must handle it.
 */
export const ENVIRONMENTS = ["dev", "integration", "staging", "prod"] as const;

export type EnvName = (typeof ENVIRONMENTS)[number];

/**
 * Fallback when `VITE_ENV_NAME` is unset or unrecognized (local builds, or a
 * misconfigured deploy). Centralized so the post-decommission default — once
 * dev / integration are torn down (ENR-986) — is a one-line change here rather
 * than a repo-wide hunt for `?? "dev"`.
 */
export const DEFAULT_ENV: EnvName = "dev";

const ENV_NAMES = new Set<string>(ENVIRONMENTS);

/** Type guard: is `value` one of the known environment names? */
export function isEnvName(value: string): value is EnvName {
  return ENV_NAMES.has(value);
}

/**
 * The ops console's own build environment (from `VITE_ENV_NAME`), normalized to
 * a known {@link EnvName}. `"production"` is treated as `"prod"`; anything
 * unrecognized (or unset) falls back to {@link DEFAULT_ENV}.
 */
export function readEnvName(): EnvName {
  const raw = (import.meta.env.VITE_ENV_NAME ?? DEFAULT_ENV).toLowerCase();
  if (raw === "production") return "prod";
  return isEnvName(raw) ? raw : DEFAULT_ENV;
}
